/**
 * Printable results sheet ("acta", /acta/:electionId): every position with its rounds, the
 * percentage base used, the stored eligible count and the winner. Clean A4 layout.
 */

import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { Loader2, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/authContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useElectionState } from '../hooks/useVoting';
import { RESULT_LABEL, candidateName, racesOfPosition, sortedResult } from '../utils/rounds';
import { votingErrorMessage } from '../utils/votingErrors';

const BASE_LABEL = { ELIGIBLE: 'habilitados para votar', VALID: 'votos válidos' } as const;
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-BO', { dateStyle: 'short', timeStyle: 'medium' }) : '—');

export function ActaPage() {
  const { electionId } = useParams<{ electionId: string }>();
  const { user } = useAuth();
  const location = useLocation();
  const { can, isLoading } = usePermissions();
  const { data, error, isLoading: loading } = useElectionState(electionId);

  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (isLoading || loading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="size-8 animate-spin text-muted-foreground" /></div>;
  if (!can('election.view')) return <p className="p-8 text-center text-muted-foreground">Sin permiso para ver esta elección.</p>;
  if (error || !data) return <p className="p-8 text-center text-destructive">{votingErrorMessage(error)}</p>;

  const state = data.state;
  const e = state.election;

  return (
    <div className="mx-auto max-w-3xl bg-white p-8 text-black print:max-w-none print:p-0">
      <div className="mb-6 flex items-center justify-between print:hidden">
        <Link to={`/voting/${e.id}`} className="text-sm underline">← Volver a la elección</Link>
        <Button onClick={() => window.print()}><Printer className="mr-2 size-4" /> Imprimir</Button>
      </div>

      <header className="mb-6 border-b-2 border-black pb-3">
        <h1 className="text-2xl font-bold">Acta de resultados · {e.name}</h1>
        {e.event_name && <p>{e.event_name}</p>}
        <p className="text-sm text-gray-600">
          Se gana con {Number(e.threshold_pct)} % de {BASE_LABEL[e.threshold_base as keyof typeof BASE_LABEL]} · Impreso el {fmt(new Date().toISOString())}
        </p>
      </header>

      {state.positions.some((p) => p.status === 'FILLED') && (
        <section className="mb-6">
          <h2 className="mb-2 text-lg font-bold">Cargos cubiertos</h2>
          <ul className="list-disc pl-6">
            {state.positions.filter((p) => p.status === 'FILLED').map((p) => (
              <li key={p.id}><strong>{p.title}:</strong> {candidateName(state, p.winner_candidate_id)}</li>
            ))}
          </ul>
        </section>
      )}

      {state.positions.map((p) => (
        <section key={p.id} className="mb-6 break-inside-avoid">
          <h2 className="mb-2 border-b border-gray-400 text-lg font-bold">{p.title}</h2>
          {racesOfPosition(state, p.id).length === 0 && <p className="text-sm text-gray-600">Sin rondas.</p>}
          {racesOfPosition(state, p.id).map((r) => (
            <div key={r.id} className="mb-4 break-inside-avoid">
              <p className="font-semibold">
                Ronda {r.round_number}
                {r.status === 'CLOSED' && r.result ? ` · ${RESULT_LABEL[r.result]}` : r.status === 'OPEN' ? ' · abierta' : ' · borrador'}
                {r.result === 'WINNER' && ` · ${candidateName(state, r.result_candidate_id)}`}
              </p>
              {r.result_data ? (
                <>
                  <table className="my-1 w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-black text-left">
                        <th className="py-1">Candidato</th><th className="w-20 text-right">Votos</th><th className="w-24 text-right">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedResult(r).map((c) => (
                        <tr key={c.candidate_id} className="border-b border-gray-300">
                          <td className="py-1">{candidateName(state, c.candidate_id)}</td>
                          <td className="text-right tabular-nums">{c.votes}</td>
                          <td className="text-right tabular-nums">{Number(c.pct).toFixed(2)} %</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-gray-700">
                    Habilitados al cierre: {r.result_data.eligible} · votaron: {r.result_data.votes_cast} · en blanco: {r.result_data.blank} · no votaron: {r.result_data.non_voters} ·
                    base del porcentaje: {r.result_data.base_count} ({BASE_LABEL[r.threshold_base]}) · {Number(r.threshold_pct)} % para ganar
                  </p>
                  <p className="text-xs text-gray-700">
                    Abierta: {fmt(r.opened_at)} · cerrada: {fmt(r.closed_at)}{r.close_reason === 'TIMER' ? ' (por tiempo)' : ''}
                    {r.winner_confirmed_at ? ` · ganador confirmado: ${fmt(r.winner_confirmed_at)}` : ''}
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-600">Candidatos: {(r.candidate_ids ?? []).map((id) => candidateName(state, id)).join(', ')}</p>
              )}
            </div>
          ))}
        </section>
      ))}

      <section className="mt-12 break-inside-avoid">
        <h2 className="mb-10 text-lg font-bold">Firmas del comité electoral</h2>
        <div className="grid grid-cols-3 gap-8 text-center text-sm">
          {['Presidente del comité', 'Secretario', 'Observador'].map((role) => (
            <div key={role}>
              <div className="border-t border-black pt-1">{role}</div>
              <div className="mt-1 text-xs text-gray-500">Nombre y firma</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
